import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PageHeader from "../PageHeader";
import { documentApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";

const MODE_NOTE = "note";
const MODE_URL = "url";

const fieldText = (value) => [].concat(value).join(" ");

function AddDocument() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(MODE_NOTE);
  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const isNote = mode === MODE_NOTE;

  const handleModeChange = (next) => {
    setMode(next);
    setFormError(null);
    setFieldErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    setFieldErrors({});
    const payload = isNote
      ? { title: title.trim(), markdown_source: markdown }
      : { url: url.trim() };
    try {
      const response = await documentApi.create(payload);
      navigate(`/knowledge/documents/${response.data.id}`);
    } catch (err) {
      const parsed = parseApiError(err, "Could not save the document.");
      setFormError(parsed.formError);
      setFieldErrors(parsed.fieldErrors);
      setSaving(false);
    }
  };

  const canSubmit = isNote ? markdown.trim().length > 0 : url.trim().length > 0;

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: "760px" }}>
      <PageHeader
        title="Add to your library"
        subtitle="Everything you save becomes instantly searchable; AI adds a summary and suggested tags moments later."
      />

      {/* Two explicit ways in — pick one, the form says exactly what
          happens next (the frontend philosophy's named-modes pattern). */}
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={mode}
        onValueChange={(next) => next && handleModeChange(next)}
        aria-label="How to save this"
        className="mb-4"
      >
        <ToggleGroupItem value={MODE_NOTE}>Write a note</ToggleGroupItem>
        <ToggleGroupItem value={MODE_URL}>Save a web page</ToggleGroupItem>
      </ToggleGroup>

      {formError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {isNote ? (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor="note-title">Title (optional)</Label>
              <Input
                id="note-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Left blank, the first heading or line becomes the title"
                aria-invalid={Boolean(fieldErrors.title) || undefined}
                aria-describedby={
                  fieldErrors.title ? "note-title-error" : undefined
                }
              />
              {fieldErrors.title && (
                <p id="note-title-error" className="text-sm text-destructive">
                  {fieldText(fieldErrors.title)}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="note-markdown">Note (markdown)</Label>
              <Textarea
                id="note-markdown"
                rows={12}
                className="max-h-[32rem]"
                value={markdown}
                onChange={(event) => setMarkdown(event.target.value)}
                placeholder={
                  "# Anything you want to keep\n\nHeadings, lists, code blocks — standard markdown."
                }
                aria-invalid={Boolean(fieldErrors.markdown_source) || undefined}
                aria-describedby={
                  fieldErrors.markdown_source
                    ? "note-markdown-error"
                    : undefined
                }
              />
              {fieldErrors.markdown_source && (
                <p
                  id="note-markdown-error"
                  className="text-sm text-destructive"
                >
                  {fieldText(fieldErrors.markdown_source)}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                You can edit the markdown any time; the note re-indexes on save.
              </p>
            </div>
          </>
        ) : (
          <div className="grid gap-1.5">
            <Label htmlFor="page-url">Page address</Label>
            <Input
              id="page-url"
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com/article"
              aria-invalid={Boolean(fieldErrors.url) || undefined}
              aria-describedby={fieldErrors.url ? "page-url-error" : undefined}
            />
            {fieldErrors.url && (
              <p id="page-url-error" className="text-sm text-destructive">
                {fieldText(fieldErrors.url)}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              The readable article is extracted and archived — fetching takes a
              few seconds. Pages that need JavaScript to show their content
              can&apos;t be captured and will say so.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={saving || !canSubmit}>
            {saving ? (
              <>
                <Loader2 className="animate-spin" />
                {isNote ? "Saving…" : "Fetching page…"}
              </>
            ) : isNote ? (
              "Save note"
            ) : (
              "Save page"
            )}
          </Button>
          {saving && !isNote && (
            <span className="text-sm text-muted-foreground">
              Downloading and extracting the article…
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddDocument;
